import { Box, Button, Center, Image, Row, Text, View } from 'native-base'
import { Platform, ViewStyle } from 'react-native'
import RenderHTML from 'react-native-render-html'
import { SafeAreaView } from 'react-native-safe-area-context'

import { ControlledField, KeyboardAwareScrollView, Spacer, LoadingStateImage } from '~components'
import { REGEX, WEB } from '~constants'
import { useDeviceInfo, useMemo, useSignInForm, useTheme, useTranslation } from '~hooks'
import { Images } from '~images'
const INPUT_WIDTH = 444

export const SignInScreen = (): JSX.Element => {
  const { t } = useTranslation()
  const { control, errors, submit, MagicLink, isSubmitting, isResolvingInvite } = useSignInForm()
  const { shouldApplyWideScreenStyles } = useDeviceInfo()
  const { colors, space } = useTheme()
  const isBusy = isSubmitting || isResolvingInvite

  const wrapperStyles = useMemo(
    (): ViewStyle => ({
      backgroundColor: colors.background,
      justifyContent: 'center',
      flexGrow: shouldApplyWideScreenStyles ? 0 : 1,
      ...(shouldApplyWideScreenStyles && {
        width: INPUT_WIDTH,
        alignSelf: 'center',
      }),
    }),
    [shouldApplyWideScreenStyles, colors]
  )

  const signatureWithImage = useMemo(
    () => (
      <Row
        w="full"
        justifyContent="center"
        mb="8"
        {...(shouldApplyWideScreenStyles && { mt: '24' })}
      >
        <Box mr="6px">
          <Text
            fontWeight="700"
            my="auto"
            {...(shouldApplyWideScreenStyles && { fontSize: 'sm', flex: 1 })}
          >
            {t('sign_in_screen.powered_by')}
          </Text>
        </Box>
        <Box>
          <Image
            source={Images.R_LOGO_WITH_NAME}
            alt="signature"
            w={shouldApplyWideScreenStyles ? '125px' : '100px'}
            flex={1}
            resizeMode="contain"
          />
        </Box>
      </Row>
    ),
    [shouldApplyWideScreenStyles, t]
  )

  return (
    <SafeAreaView style={wrapperStyles}>
      {Platform.OS === WEB ? <RenderHTML source={{ html: MagicLink }} /> : <MagicLink />}

      <KeyboardAwareScrollView
        contentContainerStyle={{
          padding: space['6'],
          width: space['full'],
        }}
      >
        <View flexGrow={2} w="full" />
        <Center flexGrow={1} w="full" {...(shouldApplyWideScreenStyles && { mt: '20' })}>
          <LoadingStateImage size={72} velocity={4000} />
          <Center w="full" pt="12">
            <Text
              bold
              fontSize="xl"
              textAlign="center"
              {...(shouldApplyWideScreenStyles && { fontSize: '3xl' })}
            >
              {t('sign_in_screen.welcome_to')}
              {shouldApplyWideScreenStyles ? '\n' : ' '}
              <Text
                bold
                color="actionBlue"
                textAlign="center"
                {...(shouldApplyWideScreenStyles && { fontSize: '3xl' })}
              >
                {t('sign_in_screen.aci_loyalty_program')}
              </Text>
            </Text>

            <Spacer y={shouldApplyWideScreenStyles ? '10' : '3'} />
            {!shouldApplyWideScreenStyles && signatureWithImage}
            {isResolvingInvite && (
              <Text mb="3" color="muted.500" textAlign="center" fontSize="sm">
                {t('sign_in_screen.resolving_invite')}
              </Text>
            )}
            <ControlledField.Input
              autoCapitalize="none"
              control={control}
              errors={errors}
              isDisabled={isResolvingInvite}
              keyboardType="email-address"
              label={t('common.email_label')}
              name="email"
              onSubmitEditing={submit}
              placeholder={
                isResolvingInvite
                  ? t('sign_in_screen.resolving_invite')
                  : t('common.email_placeholder')
              }
              returnKeyType="send"
              rules={{
                required: t('form.required'),
                pattern: {
                  value: REGEX.EMAIL,
                  message: t('form.invalid_email_format'),
                },
              }}
              testID="emailInput"
            />
          </Center>
        </Center>

        <View flexGrow={3} w="full" />
        <View w="full">
          <Button
            isDisabled={isBusy || !!errors.email}
            isLoading={isBusy}
            onPress={submit}
            testID="signInButton"
            size="lg"
            bg={colors.actionBlue}
            marginY={4}
          >
            <Text fontSize="md" color="white" bold>
              {t('sign_in_screen.log_in')}
            </Text>
          </Button>
        </View>
        {shouldApplyWideScreenStyles && signatureWithImage}
      </KeyboardAwareScrollView>
    </SafeAreaView>
  )
}
